import { useQuery } from '@tanstack/react-query';
import { marketing } from '@/lib/supabase/marketing';
import {
  computeMetric,
  getCompareRange,
  getPeriodRange,
  toISTDateString,
  type DateRange,
  type Metric,
  type PeriodKey,
} from '@/lib/periods';

interface Args {
  itemId: string;
  outletId: string | 'all';
  period: PeriodKey;
}

export interface ItemOutletRow {
  outletId: string;
  units: number;
  sales: number;
  metric: Metric;
}

export interface ItemDetailResult {
  itemName: string;
  categoryName: string | null;
  totalUnits: number;
  totalSales: number;
  unitsMetric: Metric;
  salesMetric: Metric;
  /** daily trend in current vs previous (date keyed by current period) */
  trend: { date: string; current: number; previous?: number }[];
  /** ranked breakdown of which outlets sold the item */
  outletBreakdown: ItemOutletRow[];
  current: DateRange;
  previous: DateRange;
}

async function fetchRange(itemId: string, outletId: string | 'all', range: DateRange) {
  const start = toISTDateString(range.start);
  const end = toISTDateString(new Date(range.end.getTime() - 1));
  let q = marketing
    .from('mv_item_daily')
    .select('date, outlet_id, item_name, category_name, units, gross_sales, orders')
    .eq('item_id', itemId)
    .gte('date', start)
    .lte('date', end);
  if (outletId !== 'all') q = q.eq('outlet_id', outletId);
  const { data, error } = await q;
  if (error) throw error;
  return (data ?? []) as unknown as Array<{
    date: string;
    outlet_id: string;
    item_name: string;
    category_name: string | null;
    units: number;
    gross_sales: number;
    orders: number;
  }>;
}

export function useItemDetail({ itemId, outletId, period }: Args) {
  const current = getPeriodRange(period === 'custom' ? '7d' : period);
  const previous = getCompareRange(current, 'previous');

  return useQuery<ItemDetailResult>({
    enabled: Boolean(itemId),
    queryKey: ['item-detail', itemId, outletId, current.start.toISOString()],
    queryFn: async () => {
      const [curRows, prevRows] = await Promise.all([
        fetchRange(itemId, outletId, current),
        fetchRange(itemId, outletId, previous),
      ]);

      const itemName = curRows[0]?.item_name ?? prevRows[0]?.item_name ?? itemId;
      const categoryName = curRows[0]?.category_name ?? prevRows[0]?.category_name ?? null;

      const sumUnits = (rs: typeof curRows) => rs.reduce((s, r) => s + Number(r.units ?? 0), 0);
      const sumSales = (rs: typeof curRows) =>
        rs.reduce((s, r) => s + Number(r.gross_sales ?? 0), 0);

      const totalUnits = sumUnits(curRows);
      const totalSales = sumSales(curRows);

      // daily trend (units)
      const curByDate = new Map<string, number>();
      for (const r of curRows) {
        curByDate.set(r.date, (curByDate.get(r.date) ?? 0) + Number(r.units ?? 0));
      }
      const prevByDate = new Map<string, number>();
      for (const r of prevRows) {
        prevByDate.set(r.date, (prevByDate.get(r.date) ?? 0) + Number(r.units ?? 0));
      }
      const curSeries = Array.from(curByDate.entries()).sort(([a], [b]) => a.localeCompare(b));
      const prevSeries = Array.from(prevByDate.entries()).sort(([a], [b]) => a.localeCompare(b));
      const trend = curSeries.map(([date, value], i) => ({
        date,
        current: value,
        previous: prevSeries[i]?.[1],
      }));

      // outlet breakdown
      const curByOutlet = new Map<string, { units: number; sales: number }>();
      for (const r of curRows) {
        const e = curByOutlet.get(r.outlet_id) ?? { units: 0, sales: 0 };
        e.units += Number(r.units ?? 0);
        e.sales += Number(r.gross_sales ?? 0);
        curByOutlet.set(r.outlet_id, e);
      }
      const prevByOutlet = new Map<string, number>();
      for (const r of prevRows) {
        prevByOutlet.set(
          r.outlet_id,
          (prevByOutlet.get(r.outlet_id) ?? 0) + Number(r.units ?? 0),
        );
      }
      const outletBreakdown: ItemOutletRow[] = Array.from(curByOutlet.entries())
        .map(([outletId, e]) => ({
          outletId,
          units: e.units,
          sales: e.sales,
          metric: computeMetric(e.units, prevByOutlet.get(outletId) ?? 0),
        }))
        .sort((a, b) => b.units - a.units);

      return {
        itemName,
        categoryName,
        totalUnits,
        totalSales,
        unitsMetric: computeMetric(totalUnits, sumUnits(prevRows)),
        salesMetric: computeMetric(totalSales, sumSales(prevRows)),
        trend,
        outletBreakdown,
        current,
        previous,
      };
    },
    staleTime: 60_000,
  });
}
